import React, { useMemo, useEffect, useState } from 'react';
import Select, { components as selectComponents } from 'react-select';

const ReactDropdown = ({
  options,
  value,
  onChange,
  isMulti = false,
  required = false,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  components: customComponents,
  showCheckboxes = false,
  showSelectAll = false,
  selectAllLabel = 'Select all',
  unselectAllLabel = 'Unselect all',
  paginate = false,
  pageSize = 50,
  ...props
}) => {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '42px',
      borderColor: '#e2e8f0',
      '&:hover': {
        borderColor: '#cbd5e1',
      },
    }),
    menu: (base) => ({
      ...base,
      marginTop: '0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    }),
  };

  const INTERNAL_LOAD_MORE = '__internal-load-more__';

  const LoadMoreOption = ({ children, ...props }) => {
    const isExternalLoadMore = props.data.value === 'load-more';
    const isInternalLoadMore = props.data.value === INTERNAL_LOAD_MORE;
    if (isExternalLoadMore || isInternalLoadMore) {
      return (
        <div
          className={`py-2 px-3 text-sm text-center cursor-pointer hover:bg-gray-100 ${
            isLoading ? 'text-gray-400' : 'text-blue-600'
          }`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (isExternalLoadMore) {
              if (!isLoading && typeof onLoadMore === 'function') onLoadMore();
            } else if (isInternalLoadMore) {
              setVisibleCount((prev) => prev + pageSize);
            }
          }}
        >
          {isLoading && isExternalLoadMore ? 'Loading...' : 'Load more...'}
        </div>
      );
    }
    return <selectComponents.Option {...props}>{children}</selectComponents.Option>;
  };

  // Option with checkbox for multi-selects
  const CheckboxOption = (props) => {
    if (props.data && (props.data.value === 'load-more' || props.data.value === INTERNAL_LOAD_MORE)) {
      return <LoadMoreOption {...props} />;
    }
    const { isSelected, label } = props;
    return (
      <selectComponents.Option {...props}>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={() => {}}
            onClick={(e) => e.preventDefault()}
          />
          <span>{label}</span>
        </div>
      </selectComponents.Option>
    );
  };

  // Menu list with Select all / Unselect all actions
  const MenuListWithBulkActions = (props) => {
    const { children, selectProps } = props;
    const isMultiSelect = !!selectProps.isMulti;
    const canShow = showSelectAll && isMultiSelect;
    const handleSelectAll = () => {
      const onlyOptions = (selectProps.options || []).filter(
        (opt) => opt && opt.value !== 'load-more' && opt.value !== INTERNAL_LOAD_MORE
      );
      selectProps.onChange(onlyOptions, { action: 'select-option' });
    };
    const handleUnselectAll = () => {
      selectProps.onChange([], { action: 'clear' });
    };
    return (
      <selectComponents.MenuList {...props}>
        {canShow && (
          <div className="px-3 py-2 border-b border-gray-200 flex gap-2 sticky top-0 bg-white z-10">
            <button
              type="button"
              className="text-blue-600 text-sm hover:underline"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleSelectAll}
            >
              {selectAllLabel}
            </button>
            <button
              type="button"
              className="text-gray-600 text-sm hover:underline"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleUnselectAll}
            >
              {unselectAllLabel}
            </button>
          </div>
        )}
        {children}
      </selectComponents.MenuList>
    );
  };

  // Reset visible count when options change
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [options, pageSize]);

  // Filter out 'load-more' from actual options for matching
  const actualOptions = useMemo(() => 
    (options || []).filter(opt => opt && opt.value !== 'load-more' && opt.value !== INTERNAL_LOAD_MORE),
    [options]
  );

  // Compute the options fed to react-select (external or internal pagination)
  const computedOptions = useMemo(() => {
    if (hasMore) {
      return [...actualOptions, { value: 'load-more', label: 'LOAD_MORE' }];
    }
    if (paginate) {
      const sliced = actualOptions.slice(0, Math.max(0, visibleCount));
      if (actualOptions.length > sliced.length) {
        return [...sliced, { value: INTERNAL_LOAD_MORE, label: 'LOAD_MORE' }];
      }
      return sliced;
    }
    return actualOptions;
  }, [hasMore, paginate, actualOptions, visibleCount]);

  // Normalize the value to match an option from the options array
  // This ensures react-select can properly display the selected value
  const normalizedValue = useMemo(() => {
    if (!value) return isMulti ? [] : null;
    
    if (isMulti) {
      // For multi-select, normalize each selected value
      if (!Array.isArray(value)) return [];
      return value.map(val => {
        if (!val) return null;
        // Extract the value to match
        const valToMatch = val && typeof val === 'object' && val.value !== undefined ? val.value : val;
        // Find matching option by value property
        const matchedOption = actualOptions.find(opt => {
          if (!opt || opt.value === undefined) return false;
          return String(opt.value) === String(valToMatch);
        });
        // Return the matched option from the options array, or keep original if it has proper structure
        if (matchedOption) return matchedOption;
        // Fallback: if val has value and label, use it (user just selected it)
        if (val && typeof val === 'object' && val.value !== undefined && val.label !== undefined) {
          return val;
        }
        return null;
      }).filter(Boolean);
    } else {
      // For single select, find matching option
      if (!value) return null;
      // Extract the value to match
      const valToMatch = value && typeof value === 'object' && value.value !== undefined ? value.value : value;
      // Find matching option by value property
      const matchedOption = actualOptions.find(opt => {
        if (!opt || opt.value === undefined) return false;
        return String(opt.value) === String(valToMatch);
      });
      // Return the matched option from the options array, or keep original if it has proper structure
      if (matchedOption) return matchedOption;
      // Fallback: if value has value and label, use it (user just selected it)
      if (value && typeof value === 'object' && value.value !== undefined && value.label !== undefined) {
        return value;
      }
      return null;
    }
  }, [value, actualOptions, isMulti]);

  // Custom function to compare option values for react-select v5
  const isOptionEqualToValue = (option, value) => {
    if (!option || !value) return false;
    if (option === value) return true; // Same reference
    const optionValue = option.value;
    const valueToCompare = value && typeof value === 'object' && value.value !== undefined ? value.value : value;
    return String(optionValue) === String(valueToCompare);
  };

  return (
    <Select
      options={computedOptions}
      value={normalizedValue}
      onChange={onChange}
      isMulti={isMulti}
      styles={customStyles}
      className="react-select"
      classNamePrefix="react-select"
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={isOptionEqualToValue}
      components={{
        ...customComponents,
        MenuList: MenuListWithBulkActions,
        Option: showCheckboxes && isMulti ? CheckboxOption : LoadMoreOption,
      }}
      {...props}
    />
  );
};

export default ReactDropdown; 